import React, { useCallback, useMemo, useState } from 'react';
import I18N from '@leverege/i18n';
import { Dialog, Toast } from '@leverege/ui-elements';
import { LoadingView } from '@leverege/molten/views';
import { GlobalState } from '@leverege/ui-redux';
import { Attributes } from '@leverege/ui-attributes';
import * as Sentry from '@sentry/react';
import S from './PairKeysToVehicle.css';
import { pairKeysToVehicle, unpairKeysFromVehicle } from '../../util/Interface';
import PairKeysDialog from './PairKeysDialog';
import { jsx as _jsx } from "react/jsx-runtime";
const {
  t
} = I18N.ns('recovr.actions.pairKeys.PairKeysToVehicle');

// Contains all functions and variables needed for PairKeysDialog to render 
export default function PairKeysToVehicle(props) {
  const {
    actions,
    device,
    deviceId,
    deviceVin,
    deviceKeys,
    show,
    onClose,
    getVehicleKeys
  } = props;

  // tracks the active screen and input fields the user is on to set the context
  const [activeScreen, setActiveScreen] = useState('main');
  const [activeField, setActiveField] = useState();
  const [vin, setVin] = useState(deviceVin);
  const [vehicleId, setVehicleId] = useState(deviceId);
  // current list of keys, changes with user input
  const [keys, setKeys] = useState([...deviceKeys]);
  // list of existing keys paired to current vehicle selected
  const [vehicleKeys, setVehicleKeys] = useState([...deviceKeys]);
  // data sent from onChange events 
  const [data, setData] = useState();
  const onClick = useCallback(({
    data
  }) => {
    setActiveScreen(data.screen);
    setActiveField(data.field);
  }, [setActiveScreen, setActiveField]);
  const onFocusVin = useCallback(({
    data
  }) => {
    setActiveScreen(data.screen);
    setActiveField(data.field);
    setData(data);
  }, [setActiveScreen, setActiveField]);
  const onFocusKeys = useCallback(({
    data
  }) => {
    setActiveScreen(data.screen);
    setActiveField(data.field);
    setData(data);
  }, [setActiveScreen, setActiveField]);
  const onChange = async ({
    data,
    value
  }) => {
    const {
      field,
      index,
      keyId
    } = data;
    if (field === 'keys') {
      setKeys(prevKeys => {
        // set the values for the key at the intended index
        const keysCopy = [...prevKeys];
        if (index >= 0 && index < keysCopy.length) {
          keysCopy[index] = {
            keyId,
            imagine: value
          };
        }
        return keysCopy;
      });
      // else if it's a vin, set the new vin and set its corresponding keys if it exists
    } else {
      setVin(value);
      if (value) {
        const newKeys = await getVehicleKeys(actions, data.vehicleId);
        setKeys(newKeys);
        setVehicleKeys(newKeys);
        setVehicleId(data.vehicleId);
      }
    }
    // return to the main screen, clear the active field, and update data
    setActiveScreen('main');
    setActiveField(null);
    setData(data);
  };

  // when user clicks the button to create a new key or vehicle
  const onSubmit = async ({
    value
  }) => {
    // switch up actions and translations based on if keys or vin
    let newActions = actions;
    let loading, toast, error;
    if (activeField === 'keys') {
      newActions = actions.parent().child('keys');
      loading = t('loadingKey');
      toast = t('toastKey');
      error = t('errorKey');
    } else if (activeField === 'vin') {
      loading = t('loadingVehicle');
      toast = t('toastVehicle');
      error = t('errorVehicle');
    }
    // makes request to create new key or vehicle
    try {
      const closeLoading = LoadingView.showDialog(loading);
      const res = await GlobalState.dispatch(newActions.create(value));
      closeLoading();
      Toast.success(toast);
      return res;
    } catch (err) {
      Toast.error([error, err.message]);
      Sentry.captureException(err);
      return null;
    }
  };

  // sets new values of key or vehicle in dialog after successfully creating a new one
  const onAddDevice = async evt => {
    let onChangeData;
    try {
      const res = await onSubmit(evt);
      if (activeField === 'keys') {
        // fetch the autogenerated keyId since it lags behind creation
        const keyImagineDevice = await GlobalState.dispatch(actions.parent().child('keys').item(res.id).get());
        const keyId = keyImagineDevice?.data?.keyId || keyImagineDevice?.name;
        onChangeData = {
          data: {
            field: activeField,
            index: data.index,
            keyId
          },
          value: keyImagineDevice
        };
      } else {
        onChangeData = {
          data: {
            field: activeField,
            vehicleId: res.id
          },
          value: res.data?.vin
        };
      }
      onChange(onChangeData);
      setActiveScreen(null);
      setActiveField(null);
    } catch (err) {
      console.error('Error occured while adding new device', err);
      Sentry.captureException(err);
      onClose();
    }
  };

  // deletes a key input field
  const onDeleteKey = indexToDelete => {
    const updatedList = keys.filter((_, index) => index !== indexToDelete);
    setKeys(updatedList);
  };

  // adds key input field
  const addKeyToList = () => {
    setKeys([...keys, {
      keyId: null,
      imagine: null
    }]);
  };

  // removes any blank key input fields
  const removeBlankKeyIds = keys => {
    return keys.filter(key => key.keyId !== null || key.imagine !== null);
  };

  // disable pairing button if no vin, if selected keys and existing keys are undefined, 
  // or keys selected are the same as the existing ones on the vehicle
  const disablePairing = useMemo(() => {
    const vehicleKeysIds = vehicleKeys.map(key => key.imagine?.id);
    const cleanedKeysIds = removeBlankKeyIds(keys).map(key => key.imagine?.id);
    return !vin || cleanedKeysIds[0] === undefined && vehicleKeysIds[0] === undefined || vehicleKeysIds.length === cleanedKeysIds.length && vehicleKeysIds.every(keyId => cleanedKeysIds.includes(keyId));
  }, [actions, device, deviceKeys, getVehicleKeys, keys, vehicleId, vin]);

  // pairs new keys and unpairs keys no longer there in dialog
  const onPair = async () => {
    const closeLoading = LoadingView.showDialog('Managing keys for vehicle');
    // remove any blank submissions and map it to just the imagine devices' ids
    const submittedKeys = removeBlankKeyIds(keys).map(key => key.imagine.id);
    const companyId = Attributes.get('vehicle.company', 'vehicle', device?.data) || actions.match.params.companyId;
    const locationId = Attributes.get('vehicle.location', 'vehicle', device?.data) || actions.match.params.locationId;
    let existingKeys = vehicleKeys.map(key => key.imagine?.id);
    // if there are no existing keys, set to an empty array for comparison
    if (!existingKeys[0]) {
      existingKeys = [];
    }

    // compares the submittedKeys and existingKeys by filtering out any keys from the submittedKeys that are not in the existingKeys to pair
    // and any keys from the existingKeys that are not in the submittedKeys to unpair
    const toPairKeys = submittedKeys.filter(submittedKey => !existingKeys.some(existingKey => existingKey === submittedKey));
    const toUnpairKeys = existingKeys.filter(existingKey => !submittedKeys.some(submittedKey => submittedKey === existingKey));
    if (toPairKeys.length) {
      try {
        await pairKeysToVehicle({
          companyId,
          locationId,
          vehicleId,
          keyIds: toPairKeys,
          force: true
        });
      } catch (err) {
        console.error(`Could not pair keys to vehicle: ${err.message}`);
        Sentry.captureException(err);
      }
    }
    if (toUnpairKeys.length) {
      try {
        await unpairKeysFromVehicle({
          companyId,
          locationId,
          vehicleId,
          keyIds: toUnpairKeys
        });
      } catch (err) {
        console.error(`Could not unpair keys from vehicle: ${err.message}`);
        Sentry.captureException(err);
      }
    }
    closeLoading();
    onClose();
  };
  return /*#__PURE__*/_jsx(Dialog, {
    className: S.dialog,
    show: show,
    closeOnEscape: true,
    closeOnDocumentClick: true,
    onClose: onClose,
    children: /*#__PURE__*/_jsx(PairKeysDialog, {
      ...props,
      onClick: onClick,
      onDeleteKey: onDeleteKey,
      onSubmit: onSubmit,
      keys: keys,
      addKeyToList: addKeyToList,
      activeField: activeField,
      activeScreen: activeScreen,
      vin: vin,
      data: data,
      onChange: onChange,
      onFocusKeys: onFocusKeys,
      onFocusVin: onFocusVin,
      onPair: onPair,
      onAddDevice: onAddDevice,
      removeBlankKeyIds: removeBlankKeyIds,
      disablePairing: disablePairing
    })
  });
}